import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    BarChart3, Calendar, Clock, CreditCard, ExternalLink, Hash, Headphones, Plus, RefreshCw, Shield,
    Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthProvider';
import { useSubscription } from '@/contexts/billing/subscription';
import { useBillingPortal } from '@/hooks/billing/useBillingPortal';
import { useBillingSync } from '@/hooks/billing/useBillingSync';
import { useSubscriptionUpdate } from '@/hooks/billing/useSubscriptionUpdate';

export const SubscriptionSection = () => {
  const { userProfile } = useAuth();
  const { subscription, isLoading } = useSubscription();
  const { openBillingPortal, loading: billingPortalLoading } = useBillingPortal();
  const { syncBilling, loading: syncLoading, canSync, minutesUntilNextSync } = useBillingSync();
  const { updateQuantity, loading: updateLoading, error: updateError } = useSubscriptionUpdate();
  const [showQuantitySelector, setShowQuantitySelector] = useState(false);
  const [newQuantity, setNewQuantity] = useState(1);

  const handleManageBilling = async () => {
    try {
      await openBillingPortal();
    } catch (err: any) {
      toast.error(`Failed to open billing portal: ${err.message}`);
    }
  };

  const handleUpdateQuantity = async () => {
    try {
      await updateQuantity(newQuantity);
      toast.success(`Successfully upgraded to ${newQuantity} additional ${newQuantity === 1 ? "genie" : "genies"}`);
      setShowQuantitySelector(false);
    } catch (err: any) {
      toast.error(`Failed to update quantity: ${err.message}`);
    }
  };

  const handleSyncBilling = async () => {
    try {
      const result = await syncBilling();
      toast.success(result.message);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleShowQuantitySelector = () => {
    setNewQuantity((subscription?.quantity || 0) + 1);
    setShowQuantitySelector(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "text-primary bg-primary/10";
      case "trialing":
        return "text-gray-600 bg-gray-100";
      case "canceled":
      case "past_due":
        return "text-red-700 bg-red-50";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
        <div className="flex items-center mb-[18px]">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mr-3">
            <CreditCard className="h-5 w-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Subscription</h2>
        </div>
        <div className="text-center text-gray-500">Loading subscription details...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between mb-[18px]">
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mr-3">
            <CreditCard className="h-5 w-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Subscription</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSyncBilling}
            disabled={syncLoading || !canSync}
            className="flex items-center gap-2 bg-transparent border border-gray-300 px-3 py-1.5 rounded text-sm cursor-pointer text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            title={
              !canSync
                ? `Please wait ${minutesUntilNextSync} minute${minutesUntilNextSync !== 1 ? "s" : ""} before syncing again`
                : "Refresh your subscription data from Stripe if you think it's incorrect or missing"
            }
          >
            <RefreshCw className={`h-4 w-4 ${syncLoading ? "animate-spin" : ""}`} />
            {syncLoading ? "Syncing..." : "Sync Billing Data"}
          </button>
          {subscription && (
            <button
              onClick={handleManageBilling}
              disabled={billingPortalLoading || !userProfile?.stripe_id}
              className="flex items-center gap-2 bg-transparent border border-primary/30 px-3 py-1.5 rounded text-sm cursor-pointer text-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
              title={!userProfile?.stripe_id ? "Set up billing to manage" : ""}
            >
              <ExternalLink className="h-4 w-4" />
              {billingPortalLoading ? "Loading..." : "Manage Billing"}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {!subscription ? (
          <div className="space-y-4">
            <div className="text-center">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary mb-4">
                Free Plan
              </div>
              <p className="text-gray-600 mb-4">You're currently on the free plan. Upgrade to unlock premium features!</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Zap className="h-4 w-4 text-primary" />
                <span>Unlimited conversations</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Shield className="h-4 w-4 text-primary" />
                <span>Custom branding</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <BarChart3 className="h-4 w-4 text-primary" />
                <span>Advanced analytics</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Headphones className="h-4 w-4 text-primary" />
                <span>Priority support</span>
              </div>
            </div>

            <Link
              to="/pricing"
              className="w-full inline-flex items-center justify-center gap-2 text-white border-none rounded-md px-4 py-3 font-semibold text-sm bg-primary hover:bg-primary/90 transition-colors"
            >
              View Pricing Plans
              <ExternalLink className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <>
            {/* Status and Genie Count */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Status</span>
                </div>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription.status)}`}
                >
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </span>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">Available Genies</span>
                  </div>
                  {!showQuantitySelector && (
                    <button
                      onClick={handleShowQuantitySelector}
                      className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                      title="Purchase more genies"
                    >
                      <Plus className="h-4 w-4 text-gray-600" />
                    </button>
                  )}
                </div>

                {!showQuantitySelector ? (
                  <div>
                    <span className="text-lg font-semibold text-gray-900">
                      {1 + (subscription.quantity || 0) + (subscription.extra_quantity || 0)}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">
                      {1 + (subscription.quantity || 0) + (subscription.extra_quantity || 0) === 1 ? "genie" : "genies"}
                    </span>
                    <div className="text-xs text-gray-500 mt-2">
                      1 free + {subscription.quantity || 0} from plan + {subscription.extra_quantity || 0} extra
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Purchase additional genies</label>
                      <select
                        value={newQuantity}
                        onChange={(e) => setNewQuantity(parseInt(e.target.value))}
                        className="w-full p-1.5 text-sm rounded border border-gray-300 focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      >
                        {Array.from({ length: 50 }, (_, i) => i + (subscription?.quantity || 0) + 1).map((quantity) => (
                          <option key={quantity} value={quantity}>
                            {quantity} {quantity === 1 ? "genie" : "genies"} from plan (Total:{" "}
                            {1 + quantity + (subscription.extra_quantity || 0)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpdateQuantity}
                        disabled={updateLoading || newQuantity <= (subscription.quantity || 0)}
                        className="flex-1 text-xs bg-primary text-white px-2 py-1.5 rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updateLoading ? "Upgrading..." : "Upgrade"}
                      </button>
                      <button
                        onClick={() => setShowQuantitySelector(false)}
                        className="text-xs text-gray-600 px-2 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                    {updateError && <div className="text-xs text-red-600 mt-1">{updateError}</div>}
                  </div>
                )}
              </div>
            </div>

            {/* Date Information */}
            <div className="space-y-3">
              {subscription.trial_ends_at && (
                <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-primary">Trial Ends</span>
                  </div>
                  <span className="text-sm font-medium text-primary">{formatDate(subscription.trial_ends_at)}</span>
                </div>
              )}

              {subscription.ends_at && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      {subscription.status === "active" ? "Next Billing Date" : "Ends At"}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{formatDate(subscription.ends_at)}</span>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Subscription Since</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{formatDate(subscription.created_at)}</span>
              </div>
            </div>

            {/* Status Message */}
            <div className="mt-4">
              {subscription.status === "active" ? (
                <div className="flex items-center gap-2 p-4 bg-primary/8 rounded-lg border border-primary/20">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span className="text-sm font-medium text-primary">Your subscription is active and in good standing</span>
                </div>
              ) : subscription.status === "trialing" ? (
                <div className="flex flex-col gap-3 p-4 rounded-lg border border-primary/20 bg-primary/5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                    <span className="text-sm font-medium text-primary">
                      You're on a free trial. Upgrade anytime to choose your plan and currency.
                    </span>
                  </div>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors self-start bg-primary hover:bg-primary/90"
                  >
                    View Pricing Plans
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col gap-3 p-4 rounded-lg border border-red-200 bg-red-50">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <span className="text-sm font-medium text-red-700">
                      Your subscription is not active. Choose a plan to get back up and running.
                    </span>
                  </div>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors self-start bg-primary hover:bg-primary/90"
                  >
                    View Pricing Plans
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
